import { z } from 'zod'
import { apiRequest } from './client'

// ── Zod schemas (Zod at every boundary per CLAUDE.md) ────────────────────────
//
// Pydantic serialises Decimal as a JSON string ("16.0") — not a number.
// `z.coerce.number()` accepts both shapes so the schema survives the wire
// format inconsistency documented in memory (project_lms_wire_format_inconsistent).

export const RosterBalanceSchema = z.object({
  leaveType: z.string(),
  entitlement: z.coerce.number(),
  // Accrued-so-far cap — below the yearly slice for first-year annual, equal to
  // entitlement otherwise. Lets the manager tile detect the accrual stage and
  // render an overdrawn negative balance. Optional for backward-compat with
  // fixtures predating the field.
  accruedEntitlement: z.coerce.number().optional(),
  used: z.coerce.number(),
  pending: z.coerce.number(),
  available: z.coerce.number(),
})

export const RosterLeaveSchema = z.object({
  id: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  leaveType: z.string(),
  status: z.string(),
})

export const RosterSignalSchema = z.object({
  flagCode: z.string(),
  severity: z.string(),
  tags: z.array(z.string()),
})

export const RosterMemberSchema = z.object({
  id: z.string(),
  name: z.string(),
  preferredName: z.string().nullable(),
  jobTitle: z.string(),
  // Team + parent-unit ("department") for the all/department/team scope filter.
  teamName: z.string().nullable(),
  departmentName: z.string().nullable(),
  onLeaveToday: z.boolean(),
  onLeaveThisWeek: z.boolean(),
  nextLeave: RosterLeaveSchema.nullable(),
  pendingCount: z.number(),
  health: z.object({
    score: z.number(),
    band: z.enum(['low', 'medium', 'high']),
    contributingPatterns: z.array(z.string()),
  }),
  forfeitureSeverity: z.enum(['none', 'soft', 'firm']),
  balances: z.array(RosterBalanceSchema),
  replacementBanked: z.coerce.number(),
  signals: z.array(RosterSignalSchema),
  recentLeaves: z.array(RosterLeaveSchema),
})

export const TeamTotalsSchema = z.object({
  memberCount: z.number(),
  annualAvailable: z.coerce.number(),
  annualEntitlement: z.coerce.number(),
  medicalAvailable: z.coerce.number(),
  medicalEntitlement: z.coerce.number(),
  replacementBanked: z.coerce.number(),
  replacementPeople: z.number(),
  forfeitureRiskCount: z.number(),
})

export const RosterSummarySchema = z.object({
  nextHoliday: z
    .object({ name: z.string(), date: z.string() })
    .nullable(),
  onLeaveThisMonth: z.number(),
  onLeaveThisWeek: z.number(),
})

export const TeamRosterSchema = z.object({
  members: z.array(RosterMemberSchema),
  totals: TeamTotalsSchema,
  summary: RosterSummarySchema,
  nextCursor: z.string().nullable(),
  // Exact team-member count for the filtered scope. Populated only on the
  // numbered (offset) path (`page=` supplied); null/absent on the cursor path.
  // Additive + optional so it stays backward-compatible with cursor responses.
  total: z.number().nullable().optional(),
  // Distinct team + department names across the WHOLE overseen team (never
  // narrowed by the active scope/week filter) — feeds the scope dropdown so its
  // options cover every team regardless of which page or scope is loaded.
  // Defaulted for backward-compat with responses predating the fields.
  availableTeams: z.array(z.string()).default([]),
  availableDepartments: z.array(z.string()).default([]),
})

export type TeamRoster = z.infer<typeof TeamRosterSchema>
export type RosterMember = z.infer<typeof RosterMemberSchema>
export type RosterBalance = z.infer<typeof RosterBalanceSchema>
export type RosterLeave = z.infer<typeof RosterLeaveSchema>
export type RosterSignal = z.infer<typeof RosterSignalSchema>
export type TeamTotals = z.infer<typeof TeamTotalsSchema>
export type RosterSummary = z.infer<typeof RosterSummarySchema>

// ── Fetchers ──────────────────────────────────────────────────────────────────

export async function getTeamRoster(
  params: {
    limit?: number
    before?: string
    page?: number
    onLeaveThisWeek?: boolean
    team?: string
    department?: string
  } = {},
): Promise<TeamRoster> {
  const qs = new URLSearchParams()
  if (params.limit != null) qs.set('limit', String(params.limit))
  if (params.before != null) qs.set('before', params.before)
  if (params.page != null) qs.set('page', String(params.page))
  if (params.onLeaveThisWeek) qs.set('on_leave_this_week', 'true')
  if (params.team != null) qs.set('team', params.team)
  if (params.department != null) qs.set('department', params.department)
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const raw = await apiRequest<unknown>(`/api/v1/manager/team${suffix}`)
  return TeamRosterSchema.parse(raw)
}
